/**
 * AgentRowBuilderAction — the per-row builder control for the hierarchy roster.
 *
 * Rendered via AgentHierarchyPanel's ``renderRowAction`` render-prop, so it sits inline on the
 * agent's own line (the locked design: Stop lives ON the running builder's row, not above the
 * prompt). Two states:
 *   - running builder → a compact "Stop" button (targets THIS row's runId).
 *   - awaiting builder → a "Respond" affordance; clicking it selects the row (the parent then
 *     opens that builder's interaction band above the prompt, one at a time).
 *
 * Non-builder rows and idle/done builders render nothing. Presentational only — all wiring
 * (runId, handlers, permissions) is passed down from the room.
 */
import React from 'react';
import { Loader2, Square } from 'lucide-react';

export default function AgentRowBuilderAction({
  row,
  runningRunId = null,        // set when this row is a running builder → show Stop
  isAwaiting = false,         // set when this row has an unresolved input wait → show Respond
  isSelected = false,         // the awaiting row currently bound to the band
  stopping = false,
  canRespond = true,
  onStop,                     // (runId) => void
  onRespond,                  // (agentId) => void  — selects this wait
}) {
  if (!row || !row.isBuilder) return null;

  if (runningRunId && typeof onStop === 'function') {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!stopping) onStop(runningRunId); }}
        disabled={stopping}
        className={
          'shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium '
          + (stopping
            ? 'text-slate-400'
            : 'text-rose-600 hover:bg-rose-50 focus-visible:ring-1 focus-visible:ring-rose-400')
        }
        title="Stop this builder run"
        data-testid="agent-row-stop"
      >
        {stopping
          ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          : <Square className="h-3 w-3 fill-current" aria-hidden />}
        {stopping ? 'Stopping…' : 'Stop'}
      </button>
    );
  }

  if (isAwaiting) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (canRespond && typeof onRespond === 'function') onRespond(row.agentId); }}
        disabled={!canRespond}
        className={
          'shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium '
          + (!canRespond
            ? 'text-slate-400'
            : isSelected
              ? 'bg-amber-100 text-amber-700'
              : 'text-amber-600 hover:bg-amber-50 focus-visible:ring-1 focus-visible:ring-amber-400')
        }
        title={canRespond ? 'Respond to this builder' : 'You do not have permission to respond'}
        data-testid="agent-row-respond"
      >
        {isSelected ? 'Responding' : 'Respond'}
      </button>
    );
  }

  return null;
}
